import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Sparkles, Heart } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  username?: string;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, username }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-800 rounded-3xl shadow-2xl max-w-lg w-full border border-neutral-700/50 overflow-hidden">
              {/* Header with gradient */}
              <div className="relative bg-gradient-to-r from-primary via-secondary to-accent p-8 text-center">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X size={20} className="text-white" />
                </button>

                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  className="inline-block mb-4"
                >
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                    <Sparkles size={40} className="text-white" />
                  </div>
                </motion.div>

                <h2 className="text-3xl font-bold text-white mb-2">
                  Welcome to the Tribe{username ? `, ${username}` : ''}! 🎉
                </h2>
                <p className="text-white/90 text-lg">
                  You're officially part of something real
                </p>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-gradient-to-br from-primary to-secondary rounded-lg p-2 mt-1">
                      <Heart size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">What's Vybe Tribe?</h3>
                      <p className="text-neutral-300 text-sm leading-relaxed">
                        Vybe Tribe is where you meet people who actually want to do things. Join walks,
                        coffees, workouts, nights out, or any shared interest. Real vibes. Real people.
                      </p>
                      <p className="text-neutral-300 text-sm leading-relaxed mt-2">
                        We create community Vybes to get you started, but <strong className="text-white">we encourage you to host your own</strong> —
                        that's when the magic happens.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-2 mt-1">
                      <Mail size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Check Your Email</h3>
                      <p className="text-neutral-300 text-sm leading-relaxed">
                        We've sent you a welcome email with a verification link.
                        Click it to verify your email and start joining Vybes.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Important tip box */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                  <p className="text-amber-200 text-sm">
                    <strong className="font-semibold">Tip:</strong> Don't see our email? Check your junk/spam
                    folder and add us to your contacts so you never miss important updates.
                  </p>
                </div>

                {/* CTA Button */}
                <button
                  onClick={onClose}
                  className="w-full bg-gradient-to-r from-primary via-secondary to-accent hover:shadow-lg hover:shadow-primary/50
                           text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Let's Go! 🚀
                </button>

                <p className="text-neutral-400 text-xs text-center">
                  Start browsing Vybes or create your own to meet your tribe
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WelcomeModal;
